import { TimePicker } from '@/components/ui/TimePicker';
import {
  useAutomationConfigConverter,
  useBulkUpdateAutomations,
} from '@/hooks/useAutomation';

import { useAuthStore } from '@/store/auth.store';
import { useDomainsStore } from '@/store/domains.store';
import { Folder } from '@/types/domain.types';
import { normalizeEmailToArray, validateEmails } from '@/utils/email.utils';
import {
  convertParisTimeToUserTimezone,
  getUserTimezone,
} from '@/utils/timezone';
import { AlertTriangle, Calendar, Clock, Mail, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AUTOMATION_TEMPLATES } from '../constants/automation.constants';
import {
  AutomationConfig,
  EditionConfig,
  FolderAutomations,
} from '../types/automation.types';
import { EditionManager } from './EditionManager';

interface BulkAutomationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFolders: Folder[];
  onDataRefresh?: () => void;
}

export const BulkAutomationDrawer = ({
  isOpen,
  onClose,
  selectedFolders,
  onDataRefresh,
}: BulkAutomationDrawerProps) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { clearFolderSelection } = useDomainsStore();
  const [automations, setAutomations] = useState<FolderAutomations>({});
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(
    new Set()
  );
  // Editions state for multiple editions support
  const [editions, setEditions] = useState<Record<string, EditionConfig[]>>({});

  // React Query hooks
  const bulkUpdateMutation = useBulkUpdateAutomations();
  const { convertToApiFormat } = useAutomationConfigConverter();

  // Reset state when drawer closes or folders change
  useEffect(() => {
    if (!isOpen) {
      setAutomations({});
      setSelectedTemplates(new Set());
      setEditions({});
    }
  }, [isOpen]);

  const handleTemplateToggle = (templateId: string) => {
    const newSelectedTemplates = new Set(selectedTemplates);

    if (newSelectedTemplates.has(templateId)) {
      // Remove template
      newSelectedTemplates.delete(templateId);
      const newAutomations = { ...automations };
      delete newAutomations[templateId];
      setAutomations(newAutomations);

      // Remove editions for this template
      const newEditions = { ...editions };
      delete newEditions[templateId];
      setEditions(newEditions);
    } else {
      // Add template with default config
      newSelectedTemplates.add(templateId);
      setAutomations(prev => ({
        ...prev,
        [templateId]: {
          templateId,
          email: selectedFolders[0]?.adresseEmail || '',
          dayOfMonth: 1,
          notificationEmail: user?.email || '',
          sendToEmployeePortal: false,
          sendToManagerPortal: false,
          // New execution time fields with defaults
          executionHour: 10,
          executionMinute: 0,
          retryHour: 16,
          retryMinute: 0,
          // DSN specific fields
          dsnOption: 'calculate-only',
          sendDsnByEmail: false,
          dsnEmail: selectedFolders[0]?.adresseEmail || '',
          generateBulletinOption: 'generate-only',
          customDescription: '',
          // Edition defaults (legacy support)
          editionOptionValue: '',
          editionOptionName: '',
          editionEmail: selectedFolders[0]?.adresseEmail || '',
        },
      }));

      // Initialize empty editions array for generate-edition template
      if (templateId === 'generate-edition') {
        setEditions(prev => ({
          ...prev,
          [templateId]: [],
        }));
      }
    }

    setSelectedTemplates(newSelectedTemplates);
  };

  const handleConfigChange = (
    templateId: string,
    field: keyof AutomationConfig,
    value: AutomationConfig[keyof AutomationConfig]
  ) => {
    setAutomations(prev => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        [field]: value,
      },
    }));
  };

  const handleEditionsChange = (
    templateId: string,
    newEditions: EditionConfig[]
  ) => {
    setEditions(prev => ({
      ...prev,
      [templateId]: newEditions,
    }));

    // Update the automation config with the new editions
    setAutomations(prev => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        editions: newEditions,
      },
    }));
  };

  const handleSave = async () => {
    if (selectedFolders.length === 0) return;

    try {
      // Create configurations for each selected folder
      const allConfigurations = selectedFolders.flatMap(folder => {
        // Create separate automation configs for each edition
        const allConfigs = [];

        for (const [templateId, config] of Object.entries(automations)) {
          if (
            templateId === 'generate-edition' &&
            config.editions &&
            config.editions.length > 0
          ) {
            // Create one automation config per edition
            for (const edition of config.editions) {
              const editionConfig = {
                ...config,
                templateId: 'generate-edition',
                // Override with edition-specific values
                dayOfMonth: edition.dayOfMonth,
                executionHour: edition.executionHour,
                executionMinute: edition.executionMinute,
                editionOptionValue: edition.editionOptionValue,
                editionOptionName: edition.editionOptionName,
                editionEmail: edition.editionEmail,
                usePreviousMonth: edition.usePreviousMonth || true,
                // Remove the editions array as it's not needed in the API
                editions: undefined,
              };
              allConfigs.push(editionConfig);
            }
          } else {
            // For non-edition templates, use the config as-is
            allConfigs.push(config);
          }
        }

        return convertToApiFormat(
          folder.uuid,
          folder.domainUuid,
          allConfigs.reduce((acc, config, index) => {
            acc[`${config.templateId}-${index}`] = config;
            return acc;
          }, {} as FolderAutomations)
        );
      });

      const folderUuids = selectedFolders.map(folder => folder.uuid);

      await bulkUpdateMutation.mutateAsync({
        folderUuids,
        configurations: allConfigurations,
      });

      // Clear folder selection after successful save
      clearFolderSelection();

      // Refresh parent data if callback provided
      onDataRefresh?.();

      onClose();
    } catch (error) {
      // Error handling is done in the mutation hook
      console.error('Failed to save bulk automation configs:', error);
    }
  };

  const isConfigValid = (templateId: string) => {
    const config = automations[templateId];
    if (!config) return false;

    const template = AUTOMATION_TEMPLATES.find(t => t.id === templateId);
    if (!template) return false;

    // Check required fields
    if (template.requiredFields.includes('email') && !config.email) {
      return false;
    }
    // Check DSN email if sendDsnByEmail is enabled
    if (
      templateId === 'calculate-dsn' &&
      config.sendDsnByEmail &&
      !config.dsnEmail
    ) {
      return false;
    }

    // Notification email is required
    if (!config.notificationEmail) return false;

    // Edition-specific validations
    if (templateId === 'generate-edition') {
      // Check if we have editions configured
      const templateEditions = editions[templateId] || [];
      if (templateEditions.length === 0) return false;

      // Validate each edition
      for (const edition of templateEditions) {
        if (!edition.editionOptionValue) return false;
        if (
          !edition.dayOfMonth ||
          edition.dayOfMonth < 1 ||
          edition.dayOfMonth > 31
        )
          return false;
        if (edition.executionHour < 0 || edition.executionHour > 23)
          return false;
        if (edition.executionMinute < 0 || edition.executionMinute > 59)
          return false;
        // Validate edition emails if provided
        if (edition.editionEmail) {
          const editionEmails = normalizeEmailToArray(edition.editionEmail);
          if (editionEmails.length > 0 && !validateEmails(editionEmails)) {
            return false;
          }
        }
      }
    }

    return true;
  };

  const canSave = () => {
    // if (selectedTemplates.size === 0 || selectedFolders.length === 0)
    //   return false;
    return Array.from(selectedTemplates).every(templateId => {
      return isConfigValid(templateId);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="border-b border-neutral-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-neutral-900">
                  {t('automation.bulk.title', 'Bulk Folder Automation')}
                </h2>
                <p className="text-sm text-neutral-600">
                  {t(
                    'automation.bulk.description',
                    'Configure automation for {{count}} selected folders',
                    { count: selectedFolders.length }
                  )}
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Selected Folders Summary */}
            <div className="mt-4 rounded-lg bg-neutral-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Users className="h-4 w-4 text-neutral-600" />
                <span className="text-sm font-medium text-neutral-900">
                  {t('automation.bulk.selectedFolders', 'Selected Folders')}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedFolders.slice(0, 5).map(folder => (
                  <span
                    key={folder.uuid}
                    className="inline-flex items-center rounded-full bg-neutral-200 px-2 py-1 text-xs text-neutral-700"
                  >
                    {folder.name}
                  </span>
                ))}
                {selectedFolders.length > 5 && (
                  <span className="inline-flex items-center rounded-full bg-neutral-200 px-2 py-1 text-xs text-neutral-700">
                    +{selectedFolders.length - 5} more
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Template Selection and Configuration */}
              <div>
                <h3 className="mb-4 text-lg font-semibold text-neutral-900">
                  {t('automation.templates.title', 'Choose Templates')}
                </h3>
                <p className="mb-6 text-sm text-neutral-600">
                  {t(
                    'automation.bulk.templatesDescription',
                    'Select automation templates to apply to all selected folders. Each template will be configured with the same parameters for all folders.'
                  )}
                </p>
                <div className="space-y-4">
                  {AUTOMATION_TEMPLATES.map(template => {
                    const Icon = template.icon;
                    const isSelected = selectedTemplates.has(template.id);
                    const config = automations[template.id];

                    return (
                      <div key={template.id} className="space-y-4">
                        {/* Template Selection Block */}
                        <button
                          onClick={() => handleTemplateToggle(template.id)}
                          className={`w-full rounded-xl border-2 p-4 text-left transition-all ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-neutral-200 bg-white hover:border-neutral-300'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div
                              className={`rounded-lg p-2 ${
                                isSelected
                                  ? 'bg-primary-100 text-primary-600'
                                  : 'bg-neutral-100 text-neutral-600'
                              }`}
                            >
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-semibold text-neutral-900">
                                {t(template.nameKey)}
                              </h4>
                              <p className="text-sm text-neutral-600">
                                {t(template.descriptionKey)}
                              </p>
                            </div>
                            {isSelected && (
                              <div className="bg-primary-600 flex h-5 w-5 items-center justify-center rounded-full">
                                <div className="h-2 w-2 rounded-full bg-white"></div>
                              </div>
                            )}
                          </div>
                        </button>

                        {/* Configuration Form */}
                        {isSelected && config && (
                          <div className="ml-4 space-y-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                            <div className="flex items-center gap-2 border-b border-neutral-200 pb-2">
                              <Icon className="text-primary-600 h-4 w-4" />
                              <h4 className="font-medium text-neutral-900">
                                {t(
                                  'automation.configuration.title',
                                  'Configuration'
                                )}{' '}
                                - {t(template.nameKey)}
                              </h4>
                            </div>

                            <div className="space-y-4">
                              {template.requiredFields.includes('dayOfMonth') &&
                                template.id !== 'generate-edition' && (
                                  <div>
                                    <label className="mb-2 block text-sm font-medium text-neutral-900">
                                      <Calendar className="mr-2 inline h-4 w-4" />
                                      {t(
                                        'automation.dayOfMonth',
                                        'Day of Month'
                                      )}
                                    </label>
                                    <select
                                      value={config.dayOfMonth}
                                      onChange={e =>
                                        handleConfigChange(
                                          template.id,
                                          'dayOfMonth',
                                          parseInt(e.target.value)
                                        )
                                      }
                                      className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 focus:ring-2"
                                    >
                                      {Array.from(
                                        {
                                          length:
                                            template.id === 'calculate-dsn'
                                              ? 14
                                              : template.id ===
                                                  'generate-bulletin'
                                                ? 13
                                                : template.id ===
                                                    'generate-edition'
                                                  ? 31
                                                  : 28,
                                        },
                                        (_, i) =>
                                          template.id === 'generate-bulletin'
                                            ? i + 16
                                            : template.id === 'generate-edition'
                                              ? i + 1
                                              : i + 1
                                      ).map(day => (
                                        <option key={day} value={day}>
                                          {day}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                              {/* Generate bulletin specific options */}
                              {template.id === 'generate-bulletin' && (
                                <div>
                                  <label className="mb-2 block text-sm font-medium text-neutral-900">
                                    {t(
                                      'automation.bulletin.option',
                                      'Generate Bulletin Option'
                                    )}
                                  </label>
                                  <div className="space-y-2">
                                    <label className="flex items-center gap-3">
                                      <input
                                        type="radio"
                                        value="generate-only"
                                        checked={
                                          config.generateBulletinOption ===
                                          'generate-only'
                                        }
                                        onChange={e =>
                                          handleConfigChange(
                                            template.id,
                                            'generateBulletinOption',
                                            e.target.value
                                          )
                                        }
                                        className="text-primary-600 h-4 w-4"
                                      />
                                      <span className="text-sm text-neutral-900">
                                        {t(
                                          'automation.bulletin.generateOnly',
                                          'Generate bulletin Only'
                                        )}
                                      </span>
                                    </label>
                                    <label className="flex items-center gap-3">
                                      <input
                                        type="radio"
                                        value="generate-and-send"
                                        checked={
                                          config.generateBulletinOption ===
                                          'generate-and-send'
                                        }
                                        onChange={e =>
                                          handleConfigChange(
                                            template.id,
                                            'generateBulletinOption',
                                            e.target.value
                                          )
                                        }
                                        className="text-primary-600 h-4 w-4"
                                      />
                                      <span className="text-sm text-neutral-900">
                                        {t(
                                          'automation.bulletin.generateAndSend',
                                          'Generate bulletin and send'
                                        )}
                                      </span>
                                    </label>
                                  </div>
                                </div>
                              )}
                              {template.id === 'generate-bulletin' && (
                                <div className="space-y-2">
                                  <p className="text-sm font-medium text-neutral-900">
                                    {t(
                                      'automation.bulletin.portal.title',
                                      'Portal delivery'
                                    )}
                                  </p>
                                  <p className="text-xs text-neutral-500">
                                    {t(
                                      'automation.bulletin.portal.description',
                                      'Decide whether bulletins are published to employee and manager portals.'
                                    )}
                                  </p>
                                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                                    <label className="flex items-center gap-3 text-sm text-neutral-900">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(
                                          config.sendToEmployeePortal
                                        )}
                                        onChange={e =>
                                          handleConfigChange(
                                            template.id,
                                            'sendToEmployeePortal',
                                            e.target.checked
                                          )
                                        }
                                        className="text-primary-600 h-4 w-4"
                                      />
                                      {t(
                                        'automation.bulletin.portal.employee',
                                        'Send to employee portal'
                                      )}
                                    </label>
                                    <label className="flex items-center gap-3 text-sm text-neutral-900">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(
                                          config.sendToManagerPortal
                                        )}
                                        onChange={e =>
                                          handleConfigChange(
                                            template.id,
                                            'sendToManagerPortal',
                                            e.target.checked
                                          )
                                        }
                                        className="text-primary-600 h-4 w-4"
                                      />
                                      {t(
                                        'automation.bulletin.portal.manager',
                                        'Send to manager portal'
                                      )}
                                    </label>
                                  </div>
                                </div>
                              )}

                              {/* Execution Time Configuration - only for non-edition templates */}
                              {template.requiredFields.includes(
                                'executionHour'
                              ) &&
                                template.id !== 'generate-edition' && (
                                  <div className="space-y-4">
                                    <div className="border-t border-neutral-200 pt-4">
                                      <h5 className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-900">
                                        <Clock className="h-4 w-4" />
                                        {t(
                                          'automation.executionTime.title',
                                          "Heures d'exécution"
                                        )}
                                      </h5>
                                      <p className="mb-2 text-xs text-neutral-500">
                                        {t(
                                          'automation.executionTime.description',
                                          "Configurez les heures d'exécution de vos automatisations"
                                        )}
                                      </p>
                                      <p className="mb-4 rounded-lg bg-neutral-100 p-2 text-xs text-neutral-600">
                                        <strong>
                                          {t(
                                            'automation.executionTime.timezoneNote',
                                            'Note:'
                                          )}
                                        </strong>{' '}
                                        {t(
                                          'automation.executionTime.timezoneExplanation',
                                          "Les heures sont définies en timezone Paris (Europe/Paris). L'équivalent dans votre timezone locale sera affiché ci-dessous."
                                        )}
                                      </p>

                                      <TimePicker
                                        hour={config.executionHour}
                                        minute={config.executionMinute}
                                        onHourChange={hour =>
                                          handleConfigChange(
                                            template.id,
                                            'executionHour',
                                            hour
                                          )
                                        }
                                        onMinuteChange={minute =>
                                          handleConfigChange(
                                            template.id,
                                            'executionMinute',
                                            minute
                                          )
                                        }
                                        label={t(
                                          'automation.executionTime.executionTime',
                                          "Heure d'exécution principale"
                                        )}
                                      />

                                      {/* Timezone equivalent display */}
                                      <div className="bg-primary-50 mt-3 hidden rounded-lg p-3">
                                        <div className="flex items-center gap-2 text-sm">
                                          <Clock className="text-primary-600 h-4 w-4" />
                                          <span className="text-primary-900 font-medium">
                                            {t(
                                              'automation.executionTime.equivalentInYourTimezone',
                                              'Équivalent dans votre timezone:'
                                            )}
                                          </span>
                                        </div>
                                        <div className="text-primary-700 mt-1 text-sm">
                                          {(() => {
                                            const userTimezone =
                                              getUserTimezone(user?.timezone);
                                            const timeConversion =
                                              convertParisTimeToUserTimezone(
                                                config.executionHour,
                                                config.executionMinute,
                                                userTimezone
                                              );
                                            return timeConversion.displayText;
                                          })()}
                                        </div>
                                      </div>

                                      {/* <div className="mt-4 grid grid-cols-2 gap-4">
                                      <div>
                                        <label className="mb-2 block text-xs font-medium text-neutral-700">
                                          {t(
                                            'automation.executionTime.retryHour',
                                            "Heure de retry en cas d'échec"
                                          )}
                                        </label>
                                        <select
                                          value={config.retryHour}
                                          onChange={e =>
                                            handleConfigChange(
                                              template.id,
                                              'retryHour',
                                              parseInt(e.target.value)
                                            )
                                          }
                                          className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2"
                                        >
                                          {Array.from(
                                            { length: 24 },
                                            (_, i) => (
                                              <option key={i} value={i}>
                                                {i.toString().padStart(2, '0')}h
                                              </option>
                                            )
                                          )}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="mb-2 block text-xs font-medium text-neutral-700">
                                          {t(
                                            'automation.executionTime.retryMinute',
                                            "Minute de retry en cas d'échec"
                                          )}
                                        </label>
                                        <select
                                          value={config.retryMinute}
                                          onChange={e =>
                                            handleConfigChange(
                                              template.id,
                                              'retryMinute',
                                              parseInt(e.target.value)
                                            )
                                          }
                                          className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2"
                                        >
                                          {Array.from(
                                            { length: 60 },
                                            (_, i) => (
                                              <option key={i} value={i}>
                                                {i.toString().padStart(2, '0')}
                                                min
                                              </option>
                                            )
                                          )}
                                        </select>
                                      </div>
                                    </div> */}
                                    </div>
                                  </div>
                                )}

                              {/* Email for bulletin sending */}
                              {template.requiredFields.includes('email') &&
                                template.id === 'generate-bulletin' &&
                                config.generateBulletinOption ===
                                  'generate-and-send' && (
                                  <div>
                                    <label className="mb-2 block text-sm font-medium text-neutral-900">
                                      <Mail className="mr-2 inline h-4 w-4" />
                                      {t('automation.email', 'Email Address')}
                                    </label>
                                    <input
                                      type="email"
                                      value={config.email}
                                      onChange={e =>
                                        handleConfigChange(
                                          template.id,
                                          'email',
                                          e.target.value
                                        )
                                      }
                                      placeholder="email@example.com"
                                      className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 focus:ring-2"
                                    />
                                  </div>
                                )}

                              {/* Generate edition specific options */}
                              {template.id === 'generate-edition' && (
                                <div className="space-y-4">
                                  <EditionManager
                                    folderUuid={selectedFolders[0]?.uuid || ''}
                                    editions={editions[template.id] || []}
                                    onEditionsChange={newEditions =>
                                      handleEditionsChange(
                                        template.id,
                                        newEditions
                                      )
                                    }
                                    defaultEmail={
                                      selectedFolders[0]?.adresseEmail || ''
                                    }
                                  />
                                </div>
                              )}

                              {/* DSN specific options */}
                              {template.id === 'calculate-dsn' && (
                                <div className="space-y-4">
                                  <div>
                                    <label className="mb-2 block text-sm font-medium text-neutral-900">
                                      {t('automation.dsn.option', 'DSN Option')}
                                    </label>
                                    <div className="space-y-2">
                                      <label className="flex items-center gap-3">
                                        <input
                                          type="radio"
                                          value="calculate-only"
                                          checked={
                                            config.dsnOption ===
                                            'calculate-only'
                                          }
                                          onChange={e =>
                                            handleConfigChange(
                                              template.id,
                                              'dsnOption',
                                              e.target.value
                                            )
                                          }
                                          className="text-primary-600 h-4 w-4"
                                        />
                                        <span className="text-sm text-neutral-900">
                                          {t(
                                            'automation.dsn.calculateDsn',
                                            'Calculate DSN'
                                          )}
                                        </span>
                                      </label>
                                      <label className="flex items-center gap-3">
                                        <input
                                          type="radio"
                                          value="calculate-and-send"
                                          checked={
                                            config.dsnOption ===
                                            'calculate-and-send'
                                          }
                                          onChange={e =>
                                            handleConfigChange(
                                              template.id,
                                              'dsnOption',
                                              e.target.value
                                            )
                                          }
                                          className="text-primary-600 h-4 w-4"
                                        />
                                        <span className="text-sm text-neutral-900">
                                          {t(
                                            'automation.dsn.calculateAndSend',
                                            'Calculate DSN and Send to net entreprise'
                                          )}
                                        </span>
                                      </label>
                                    </div>
                                  </div>

                                  {/* DSN Email Configuration */}
                                  <div className="border-t border-neutral-200 pt-4">
                                    <label className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-900">
                                      <input
                                        type="checkbox"
                                        checked={config.sendDsnByEmail || false}
                                        onChange={e =>
                                          handleConfigChange(
                                            template.id,
                                            'sendDsnByEmail',
                                            e.target.checked
                                          )
                                        }
                                        className="text-primary-600 h-4 w-4"
                                      />
                                      {t(
                                        'automation.dsn.sendByEmail',
                                        'Envoyer DSN par email'
                                      )}
                                    </label>

                                    {config.sendDsnByEmail && (
                                      <div className="mt-3">
                                        <label className="mb-2 block text-xs font-medium text-neutral-700">
                                          {t(
                                            'automation.dsn.emailAddress',
                                            "Adresse email pour l'envoi DSN"
                                          )}
                                        </label>
                                        <input
                                          type="email"
                                          value={config.dsnEmail || ''}
                                          onChange={e =>
                                            handleConfigChange(
                                              template.id,
                                              'dsnEmail',
                                              e.target.value
                                            )
                                          }
                                          placeholder="dsn@entreprise.com"
                                          className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Notification Email */}
                              <div>
                                <label className="mb-2 block text-sm font-medium text-neutral-900">
                                  <AlertTriangle className="mr-2 inline h-4 w-4" />
                                  {t(
                                    'automation.notificationEmail',
                                    'Notification Email (for failures)'
                                  )}
                                </label>
                                <input
                                  type="email"
                                  value={config.notificationEmail}
                                  onChange={e =>
                                    handleConfigChange(
                                      template.id,
                                      'notificationEmail',
                                      e.target.value
                                    )
                                  }
                                  placeholder="notifications@example.com"
                                  className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-4 py-3 focus:ring-2"
                                />
                                <p className="mt-1 text-xs text-neutral-500">
                                  {t(
                                    'automation.notificationEmail.description',
                                    'Required for failure notifications'
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-neutral-200 p-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-neutral-600">
                {selectedTemplates.size > 0 && selectedFolders.length > 0 && (
                  <span>
                    {selectedTemplates.size} template(s) ×{' '}
                    {selectedFolders.length} folder(s) ={' '}
                    {selectedTemplates.size * selectedFolders.length}{' '}
                    configurations
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!canSave() || bulkUpdateMutation.isPending}
                  className="bg-primary-600 hover:bg-primary-700 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {bulkUpdateMutation.isPending
                    ? t('common.loading', 'Loading...')
                    : t('automation.bulk.save', 'Apply to All Folders')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
